import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera, Send, Loader2, CheckCircle, XCircle, BookOpen,
  Lightbulb, ArrowRight, Save, Trash2, ChevronDown, ChevronUp,
  Image, Type, Sparkles
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';

interface SolutionStep {
  step: number;
  description: string;
  formula?: string;
}

interface SolutionResult {
  question: string;
  answer: string;
  steps: SolutionStep[];
  explanation: string;
  keyPoints: string[];
}

interface SavedMistake {
  id: number;
  question: string;
  answer: string;
  savedAt: string;
}

type InputMode = 'text' | 'photo';

export function AISolverPage() {
  const { user } = useAuthStore();
  const [inputMode, setInputMode] = useState<InputMode>('text');
  const [question, setQuestion] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [solution, setSolution] = useState<SolutionResult | null>(null);
  const [error, setError] = useState('');
  const [savedMistakes, setSavedMistakes] = useState<SavedMistake[]>([]);
  const [showSavedList, setShowSavedList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setImagePreview(base64);
      setImageBase64(base64.split(',')[1]);
    };
    reader.readAsDataURL(file);
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64 = event.target?.result as string;
            setImagePreview(base64);
            setImageBase64(base64.split(',')[1]);
            setInputMode('photo');
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  };

  const handleSubmit = async () => {
    if (!question && !imageBase64) {
      setError('请输入问题或上传图片');
      return;
    }

    setLoading(true);
    setError('');
    setSolution(null);
    setSaved(false);

    try {
      let questionText = question;

      if (imageBase64) {
        const ocrRes = await fetch('/api/public/ocr', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: imageBase64 }),
        });
        const ocrData = await ocrRes.json();
        
        if (ocrData.text) {
          questionText = questionText ? `${questionText}\n\n[图片内容]: ${ocrData.text}` : ocrData.text;
        }
      }

      const res = await fetch('/api/public/solver/solve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-User-Id': user?.id || 'anonymous',
        },
        body: JSON.stringify({
          question: questionText,
          subject: 'Mathematics',
          grade: 10,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || '解题失败');
      }

      setSolution(data.data);
    } catch (err: any) {
      setError(err.message || '解题失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToMistakes = async () => {
    if (!solution || saving || saved) return;

    setSaving(true);
    try {
      const res = await fetch('/api/public/mistakes/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-User-Id': user?.id || 'anonymous',
        },
        body: JSON.stringify({
          question_text: solution.question,
          answer: solution.answer,
          explanation: solution.explanation,
          subject: 'Mathematics',
          grade: 10,
          source: 'ai_solver',
        }),
      });

      const data = await res.json();

      if (res.ok) {
        setSaved(true);
        setSavedMistakes(prev => [{
          id: data.data?.id || Date.now(),
          question: solution.question,
          answer: solution.answer,
          savedAt: new Date().toISOString(),
        }, ...prev]);
      }
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setQuestion('');
    setImagePreview(null);
    setImageBase64(null);
    setSolution(null);
    setError('');
    setSaved(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full text-white text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            AI智能解题
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2">AI解题助手</h1>
          <p className="text-slate-600">拍照或输入问题，获取详细解题步骤，自动保存到错题本</p>
        </motion.div>

        {/* Input Mode Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center gap-2 mb-6"
        >
          <button
            onClick={() => setInputMode('text')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              inputMode === 'text'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
          >
            <Type className="w-4 h-4" />
            文字输入
          </button>
          <button
            onClick={() => setInputMode('photo')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              inputMode === 'photo'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
          >
            <Camera className="w-4 h-4" />
            拍照上传
          </button>
        </motion.div>

        {/* Input Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 mb-6"
        >
          <AnimatePresence mode="wait">
            {inputMode === 'text' ? (
              <motion.div
                key="text"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
              >
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  输入你的问题
                </label>
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onPaste={handlePaste}
                  placeholder="例如：解方程 2x + 5 = 13&#10;&#10;提示：也可以直接粘贴截图..."
                  className="w-full h-40 px-4 py-3 border border-slate-200 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                />
              </motion.div>
            ) : (
              <motion.div
                key="photo"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  上传题目图片
                </label>
                
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full max-h-64 object-contain rounded-xl border border-slate-200"
                    />
                    <button
                      onClick={() => {
                        setImagePreview(null);
                        setImageBase64(null);
                      }}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all"
                  >
                    <Camera className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">点击上传或拍照</p>
                    <p className="text-slate-400 text-sm mt-1">支持 JPG, PNG 格式</p>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleImageSelect}
                  className="hidden"
                />

                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    补充说明（可选）
                  </label>
                  <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="例如：请用因式分解的方法"
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              {error}
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleClear}
              disabled={loading}
              className="px-4 py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || (!question && !imageBase64)}
              className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  解题中...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  开始解题
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* Solution Result */}
        <AnimatePresence>
          {solution && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden mb-6"
            >
              {/* Question */}
              <div className="p-6 border-b border-slate-100">
                <h3 className="text-sm font-medium text-slate-500 mb-2">问题</h3>
                <p className="text-slate-800 font-medium">{solution.question}</p>
              </div>

              {/* Answer */}
              <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-slate-100">
                <h3 className="text-sm font-medium text-green-600 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  答案
                </h3>
                <p className="text-2xl font-bold text-green-700">{solution.answer}</p>
              </div>

              {/* Steps */}
              <div className="p-6 border-b border-slate-100">
                <h3 className="text-sm font-medium text-slate-500 mb-4 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4" />
                  解题步骤
                </h3>
                <div className="space-y-4">
                  {solution.steps?.map((step, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex gap-4"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-sm">
                        {step.step}
                      </div>
                      <div className="flex-1">
                        <p className="text-slate-700">{step.description}</p>
                        {step.formula && (
                          <div className="mt-2 p-3 bg-slate-50 rounded-lg font-mono text-blue-600">
                            {step.formula}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Key Points */}
              {solution.keyPoints && solution.keyPoints.length > 0 && (
                <div className="p-6 bg-amber-50 border-b border-slate-100">
                  <h3 className="text-sm font-medium text-amber-600 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    关键知识点
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {solution.keyPoints.map((point, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-white border border-amber-200 rounded-full text-sm text-amber-700"
                      >
                        {point}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Explanation */}
              {solution.explanation && (
                <div className="p-6 border-b border-slate-100">
                  <h3 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    详细解释
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{solution.explanation}</p>
                </div>
              )}

              {/* Save to Mistakes */}
              <div className="p-6 bg-slate-50">
                <button
                  onClick={handleSaveToMistakes}
                  disabled={saving || saved}
                  className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                    saved
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600'
                  } disabled:opacity-50`}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      保存中...
                    </>
                  ) : saved ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      已保存到错题本
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      保存到错题本
                    </>
                  )}
                </button>
                <p className="text-center text-slate-500 text-sm mt-2">
                  保存后可在错题本中查看和复习
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Saved Mistakes List */}
        {savedMistakes.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden"
          >
            <button
              onClick={() => setShowSavedList(!showSavedList)}
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <span className="font-medium text-slate-700">
                本次已保存 ({savedMistakes.length})
              </span>
              {showSavedList ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </button>
            
            <AnimatePresence>
              {showSavedList && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="border-t border-slate-100"
                >
                  {savedMistakes.map((mistake) => (
                    <div
                      key={mistake.id}
                      className="p-4 border-b border-slate-100 last:border-b-0"
                    >
                      <p className="text-slate-800 text-sm line-clamp-2">
                        {mistake.question}
                      </p>
                      <p className="text-green-600 text-sm mt-1 font-medium">
                        答案: {mistake.answer}
                      </p>
                      <p className="text-slate-400 text-xs mt-1">
                        {new Date(mistake.savedAt).toLocaleString('zh-CN')}
                      </p>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100"
        >
          <h4 className="font-medium text-blue-800 mb-2">💡 使用提示</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• 文字输入时，尽量描述清楚题目的所有条件</li>
            <li>• 拍照时请确保图片清晰，光线充足</li>
            <li>• 解题后会自动保存到错题本，方便后续复习</li>
            <li>• 支持直接粘贴截图（Ctrl+V / Cmd+V）</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
}
